import { inject, NetworkException } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { GridStore, ResetAction, RefreshAction, FetchListAction } from "./components/grid";
import { AtsOrganizationTemplate } from "models/ats-organization-template";
import { AtsTemplateRepository } from "network/ats-template-repository";

const REQUIRED_GRID_FIELDS = "name,metaData.activeVersion,metaData.lastPulledUtc,metaData.type";
const CACHE_KEY = "ats-template-list-store";

export class EnsureAction {}
export class PreviousPageAction {}
export class NextPageAction {}

@inject
export class AtsTemplateListStore extends GridStore<AtsOrganizationTemplate, null> {
    private dataLoading: boolean = false;

    constructor(private network: AtsTemplateRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private startAction(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(EnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once("ensure-ats-template-list", async () => {
            if (this.state.loaded) return;

            this.restoreState();
            this.loadPageData();
        });
    }

    @handle(RefreshAction)
    private refreshAction(action: RefreshAction) {
        this.handleRefreshAction(action, () => {
            this.restoreState();
            this.loadPageData();
        });
    }

    @handle(ResetAction)
    private resetAction(action: ResetAction) {
        this.handleResetAction(action, () => {
            this.loadPageData();
        });
    }

    @handle(FetchListAction)
    private fetchListAction(action: FetchListAction<null>) {
        this.handleFetchList(action, () => {
            this.loadPageData();
        });
    }

    @handle(NextPageAction)
    private nextPageAction(action: NextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData();
        });
    }

    @handle(PreviousPageAction)
    private previousPageAction(action: NextPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData();
        });
    }

    private async loadPageData() {
        if (this.state.loaded || this.dataLoading) return;
        this.dataLoading = true;
        const { search, sort, pageSize, pageNumber } = this.state;

        try {
            const res = await this.network.list(null, search, pageNumber, pageSize, sort, REQUIRED_GRID_FIELDS);

            this.setState((state) => ({
                ...state,
                loaded: true,
                list: res.result,
                total: res.total,
                errorLoading: false,
                errorMessage: "",
                updatedDate: new Date()
            }));

            this.saveState();
        } catch (err) {
            let message = "Error loading configuration templates data";
            if (err instanceof NetworkException) {
                message += `: ${err.result?.message}`;
            }

            this.setState((state) => ({ ...state, list: [], loaded: true, errorLoading: true, errorMessage: message }));
        } finally {
            this.dataLoading = false;
        }
    }
}
